import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Select, Tooltip } from "antd";
import {
   CardCvcElement,
   CardExpiryElement,
   CardNumberElement,
   useElements,
   useStripe
} from "@stripe/react-stripe-js";
import { httpService } from "../../../base/services/httpService.service";
import AuthService from "../../../base/services/authentication.service";
import { SessionService } from "../../../base/services/session.service";
import { getRegisteringAccount } from "../register/registrationSession";
import { deriveCountryCode } from "../register/registrationDefaults";
import { useRecaptchaHeaders } from "../../../hooks/useRecaptchaHeaders";
import { CARD_ELEMENT_OPTIONS } from "./stripeElements";
import "./paymentForm.scss";

// The flow before the new billing flow. With Billing:NewFlow on, a card is saved
// through a SetupIntent instead (components/Billing/CardSetup.jsx), and nothing
// mounts this form; it goes when the flag does.

// The one form a card is given to KAPsurvey on, wherever that happens: the
// payment step of a sign up that has come back to pay, the same step of an
// account whose subscription has run out, and the subscription page of an
// account that wants its card on file before the free trial ends.
//
// It asks for the billing address as well as the card, because that is what an
// invoice is made out to and what a bank checks a card against. The address,
// and the name the card is in, are asked for in the app's own fields — the same
// ones, worded and ordered the same way, that this form has always asked; none
// of it is a card detail, so none of it has to be Stripe's to collect. The card
// itself does: the number, the expiry and the security code are each mounted in
// an iframe of Stripe's, so no card detail ever reaches this application.
//
// The address travels with the card: it is set on the payment method as it is
// created, which is the object the server then files against the organization.

/** Who the card is being filed for, whether or not anybody is signed in yet. */
const billingAccount = () => AuthService.getUser() ?? getRegisteringAccount() ?? {};

/**
 * The name on the card, offered as the two fields it is asked for in. The
 * account gave one full name when it registered, so the first word is offered
 * as the first name and whatever follows as the last — an offer, not an answer:
 * the name that matters here is the one the bank has, which is the cardholder's
 * to correct.
 */
const splitName = (fullName) => {
   const [firstName = "", ...rest] = (fullName ?? "").trim().split(/\s+/);

   return { firstName, lastName: rest.join(" ") };
};

const CVV_HINT =
   "The three digits on the back of your card, just after the signature strip — " +
   "or, on American Express, the four printed on the front.";

export default function CheckoutForm({
   handleOk = () => {},
   handleCardDetails = () => {},
   submitLabel = "Add payment method"
}) {
   const stripe = useStripe();
   const elements = useElements();
   const recaptchaHeaders = useRecaptchaHeaders();
   // Null until the list has been answered for, one way or the other, which is
   // what the select spins over.
   const [countries, setCountries] = useState(null);
   const [message, setMessage] = useState(null);
   const [isLoading, setIsLoading] = useState(false);

   // Read once. Nothing that fills this form in can change while it is open,
   // and antd only reads its initial values on the first render anyway.
   const [account] = useState(billingAccount);
   const organizationId = AuthService.getOrganizationId() ?? SessionService.getItem("currentOrgId");
   const phone = account.phoneNumber ?? account.phone;

   // The countries the billing address can name. Fetched rather than held here
   // because it is the same list the rest of the app fills a country field from.
   useEffect(() => {
      httpService
         .get(
            `/Languages/getAllCountries`,
            (res) => setCountries(res.data ?? []),
            () => setCountries([])
         )
         .catch(() => {
            // Already answered above; the rejection is not news.
         });
   }, []);

   // Stripe files an address under the two-letter country code, which is what
   // the list is keyed on here. A country the list reports without one is left
   // out rather than offered as a choice that cannot be sent.
   const countryOptions = useMemo(
      () =>
         (countries ?? [])
            .filter((country) => country.code)
            .map((country) => ({ value: country.code.toUpperCase(), label: country.name }))
            .sort((a, b) => a.label.localeCompare(b.label)),
      [countries]
   );

   // The country the account's phone number was dialled from, which is the only
   // thing sign up ever learned about where it is. Wrong as often as it is
   // right, so it is filled in rather than assumed — the field is still there
   // to be changed.
   const initialValues = useMemo(
      () => ({
         ...splitName(account.fullName ?? account.fullname),
         country: deriveCountryCode(phone)
      }),
      [account.fullName, account.fullname, phone]
   );

   const fail = (reason) => {
      setMessage(reason);
      setIsLoading(false);
   };

   // The card is tokenized by Stripe and only its id reaches us; this hands
   // that id to the server, which attaches it to the organization's customer.
   // The endpoint is rate limited now and refuses a request without the
   // reCAPTCHA header. When no token can be had, nothing is sent and the
   // rejection says why (the submit below shows it).
   const savePaymentMethod = async (paymentMethodId) => {
      const params = new URLSearchParams({ paymentMethodId, organizationId });
      const headers = await recaptchaHeaders();

      httpService
         .get(
            `/Payment/addPaymentMethodToOrganization?${params.toString()}`,
            (response) => {
               if (!response) return fail("We couldn't save your card. Please try again.");

               setIsLoading(false);
               // The organization's registered level comes back with the
               // payment method. It is the only place the plan step that
               // follows can learn it from, because nobody is signed in yet.
               handleCardDetails(response.data?.organizationLevel);
               handleOk();
            },
            (error) => {
               // A refusal that came back on a 200 has already been shown by
               // the response interceptor, so it is not said twice — only the
               // form is put back the way it was. Anything else reaches here
               // unreported, and is said without reaching through a shape the
               // failure may not have: a dropped connection carries no response
               // at all.
               if (error?.isSuccess === false) {
                  setIsLoading(false);
                  return;
               }

               fail(
                  error?.response?.data?.message ??
                     error?.message ??
                     "We couldn't save your card. Please try again."
               );
            },
            { headers }
         )
         .catch(() => {
            // Already reported in the callback above; the rejection is not news.
         });
   };

   // Only reached once the billing fields have passed their own rules; what is
   // left to check is the card, which Stripe checks as it reads it.
   const handleSubmit = async (values) => {
      // Stripe.js hasn't loaded yet, or a submit has already been answered.
      if (!stripe || !elements || isLoading) return;

      if (!organizationId) {
         setMessage("We couldn't tell which account to save this card for. Please sign in again.");
         return;
      }

      const cardElement = elements.getElement(CardNumberElement);

      // Stripe's fields are mounted in iframes of their own, and a submit that
      // beat one of them to it has nothing to read.
      if (!cardElement) {
         setMessage("The payment form is still loading. Please try again in a moment.");
         return;
      }

      setIsLoading(true);
      setMessage(null);

      try {
         const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
               name: `${values.firstName} ${values.lastName}`.trim(),
               address: {
                  line1: values.address?.trim(),
                  city: values.city?.trim(),
                  // Left off rather than sent empty: plenty of countries have
                  // nothing to put in it.
                  ...(values.state?.trim() ? { state: values.state.trim() } : {}),
                  postal_code: values.postalCode?.trim(),
                  country: values.country
               },
               ...(account.email ? { email: account.email } : {}),
               ...(phone ? { phone } : {})
            }
         });

         // An incomplete card says which part of it is missing, so the refusal
         // is passed on as Stripe worded it.
         if (error || !paymentMethod?.id) {
            return fail(error?.message ?? "Please check your card details and try again.");
         }

         await savePaymentMethod(paymentMethod.id);
      } catch (error) {
         fail(error?.message ?? "An unexpected error occurred. Please try again.");
      }
   };

   return (
      <div className="payment-form">
         <Form
            layout="vertical"
            requiredMark={false}
            initialValues={initialValues}
            onFinish={handleSubmit}
            scrollToFirstError
         >
            <section className="payment-form__section">
               <h3 className="payment-form__title">Billing information</h3>

               <Form.Item
                  name="address"
                  label="Address / Street"
                  rules={[
                     {
                        required: true,
                        whitespace: true,
                        message: "Please enter your billing address"
                     }
                  ]}
               >
                  <Input size="large" placeholder="Address / Street" />
               </Form.Item>

               <div className="payment-form__row">
                  <Form.Item
                     name="city"
                     label="City"
                     rules={[
                        { required: true, whitespace: true, message: "Please enter your city" }
                     ]}
                  >
                     <Input size="large" placeholder="City" />
                  </Form.Item>
                  {/* Not insisted on: a state or province is something only some
                      countries have, and a card is not refused for want of one. */}
                  <Form.Item name="state" label="State / Province">
                     <Input size="large" placeholder="State / Province" />
                  </Form.Item>
               </div>

               <div className="payment-form__row">
                  <Form.Item
                     name="postalCode"
                     label="ZIP / Postal code"
                     rules={[
                        {
                           required: true,
                           whitespace: true,
                           message: "Please enter your ZIP or postal code"
                        }
                     ]}
                  >
                     <Input size="large" placeholder="ZIP / Postal code" />
                  </Form.Item>
                  <Form.Item
                     name="country"
                     label="Country"
                     rules={[{ required: true, message: "Please select your country" }]}
                  >
                     <Select
                        size="large"
                        showSearch
                        placeholder="Select your country"
                        optionFilterProp="label"
                        options={countryOptions}
                        loading={countries === null}
                        notFoundContent={countries === null ? null : "No countries found"}
                        filterOption={(input, option) =>
                           option.label.toLowerCase().includes(input.toLowerCase())
                        }
                     />
                  </Form.Item>
               </div>
            </section>

            <section className="payment-form__section">
               <h3 className="payment-form__title">Payment information</h3>

               <div className="payment-form__row">
                  <Form.Item
                     name="firstName"
                     label="First name"
                     rules={[
                        {
                           required: true,
                           whitespace: true,
                           message: "Please enter the first name on the card"
                        }
                     ]}
                  >
                     <Input size="large" placeholder="First name" />
                  </Form.Item>
                  <Form.Item
                     name="lastName"
                     label="Last name"
                     rules={[
                        {
                           required: true,
                           whitespace: true,
                           message: "Please enter the last name on the card"
                        }
                     ]}
                  >
                     <Input size="large" placeholder="Last name" />
                  </Form.Item>
               </div>

               <label className="payment-form__label" htmlFor="card-number">
                  Card number
               </label>
               <div className="payment-form__field" id="card-number">
                  <CardNumberElement
                     options={{ ...CARD_ELEMENT_OPTIONS, showIcon: true }}
                     // A card being typed says what is wrong with it as it goes,
                     // and takes it back once it is not wrong any more.
                     onChange={({ error }) => setMessage(error ? error.message : null)}
                  />
               </div>

               <div className="payment-form__row">
                  <div>
                     {/* Month and year in one field, which is the only shape
                         Stripe lends an expiry date out in. */}
                     <label className="payment-form__label" htmlFor="card-expiry">
                        Expiry date
                     </label>
                     <div className="payment-form__field" id="card-expiry">
                        <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
                     </div>
                  </div>
                  <div>
                     <div className="payment-form__label-row">
                        <label className="payment-form__label" htmlFor="card-cvc">
                           CVV
                        </label>
                        <Tooltip title={CVV_HINT}>
                           <span className="payment-form__hint" tabIndex={0}>
                              What is CVV?
                           </span>
                        </Tooltip>
                     </div>
                     <div className="payment-form__field" id="card-cvc">
                        <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
                     </div>
                  </div>
               </div>
            </section>

            {message && (
               <div className="payment-form__message" id="payment-message" role="alert">
                  {message}
               </div>
            )}

            <Button
               htmlType="submit"
               className="kapSurveyPrimaryButton payment-form__submit"
               disabled={!stripe || !elements || isLoading}
               loading={isLoading}
            >
               {submitLabel}
            </Button>

            <p className="payment-form__note">
               Your card details are handled by Stripe and never reach KAPsurvey.
            </p>
         </Form>
      </div>
   );
}
